import React from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import EvidenceRefRow from './EvidenceRefRow';
import { EVIDENCE_STATE_PRESENTATION, resolveAvailabilityBadge, resolveEvidenceState } from './evidenceState';
import type { EvidenceErrorKind } from './useFailureModeEvidence';
import { ANCHOR_MATURITIES, CONVERSION_REASON_CODES } from '../../types/evidence';
import type { EvidenceRef, LegacyConversionNote, ReviewFinding } from '../../types/evidence';

interface EvidenceRefListProps {
  finding: ReviewFinding | null;
  /** In flight: must not render as "no evidence" — that states a result it does not have. */
  loading?: boolean;
  /** Parts of the source payload the conversion could not carry into evidence. */
  notes?: LegacyConversionNote[];
  /** A load failure is its own state — it must never render as "no evidence" (invariant 4). */
  error?: Error | null;
  /** Which failure: a retryable one, a permanent one, a permission denial, or a contract
      mismatch — each with its own copy. Omitted = retryable, the transport's own default;
      the hook always sets it alongside `error`. */
  errorKind?: EvidenceErrorKind;
  onJump?: (evidenceRef: EvidenceRef) => void;
}

type TFunction = ReturnType<typeof useTranslation>['t'];

// Never interpolate a raw wire string into a key: i18next reads ':' as a namespace and '.'
// as a path separator, so an unknown code could resolve to an unrelated shipped string.
const isKnownReasonCode = (code: string): boolean =>
  (CONVERSION_REASON_CODES as readonly string[]).includes(code);

const isKnownMaturity = (maturity: string): boolean =>
  (ANCHOR_MATURITIES as readonly string[]).includes(maturity);

/** What the conversion could not carry, stated by wire reason code — never server prose. */
const ConversionNotes: React.FC<{ notes: LegacyConversionNote[]; t: TFunction }> = ({ notes, t }) => {
  if (notes.length === 0) return null;

  return (
    <div className="rounded-md border border-dashed p-3">
      <p className="revy-cjk text-xs font-medium">{t('evidence.notCarried')}</p>
      <ul className="mt-1 list-disc pl-4">
        {notes.map((note, index) => (
          <li key={index} className="revy-cjk text-xs leading-[1.6] text-muted-foreground">
            {t(isKnownReasonCode(note.reasonCode)
              ? `evidence.conversionNote.${note.reasonCode}`
              : 'evidence.conversionNote.unknown')}
          </li>
        ))}
      </ul>
    </div>
  );
};

const ERROR_COPY: Record<EvidenceErrorKind, string> = {
  retryable: 'evidence.error',
  permanent: 'evidence.errorPermanent',
  permissionDenied: 'evidence.errorPermissionDenied',
  contract: 'evidence.contractMismatch',
};

const EvidenceRefList: React.FC<EvidenceRefListProps> = ({
  finding, notes = [], error, errorKind = 'retryable', loading = false, onJump,
}) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <p role="status" aria-busy="true" className="revy-cjk text-sm leading-[1.6] text-muted-foreground">
        {t('evidence.loading')}
      </p>
    );
  }

  if (error) {
    return (
      <Alert variant="warning" className="revy-ui">
        <AlertDescription className="revy-cjk leading-[1.6]">
          {t(ERROR_COPY[errorKind])}
        </AlertDescription>
      </Alert>
    );
  }

  // Invariant 2: a card that cannot name the generator that produced it is not
  // reproducible. Its own message — retrying, which `evidence.error` advises, cannot help.
  if (finding && !finding.provenance.generatorVersion) {
    return (
      <Alert variant="warning" className="revy-ui">
        <AlertDescription className="revy-cjk leading-[1.6]">
          {t('evidence.unrenderable')}
        </AlertDescription>
      </Alert>
    );
  }

  // Notes render even with no evidence: that is exactly when they exist, because the
  // adapter emits a note INSTEAD of a ref for every reference it could not carry
  // (`application/evidence/legacy_adapters.py:489-500`). Dropping them here would state
  // "no evidence" while discarding the only explanation of why.
  if (!finding) {
    return (
      <Alert variant="info" className="revy-ui">
        <AlertDescription className="revy-cjk leading-[1.6]">{t('evidence.empty')}</AlertDescription>
      </Alert>
    );
  }

  if (finding.evidence.length === 0) {
    // An empty list is not automatically "nothing yet". `_status_for` folds a finding to
    // `unsupported` when any reference could not be carried
    // (`application/evidence/legacy_adapters.py:158`), and a failure mode with no references
    // at all is redacted to `claim: null` — the withheld state. Both must keep their own
    // meaning here, so the same resolver that labels a populated card labels this one.
    const state = resolveEvidenceState(finding);
    const degraded = state !== 'verified' && state !== 'unverified';
    return (
      <section className="flex flex-col gap-3">
        <Alert variant={degraded ? 'warning' : 'info'} className="revy-ui">
          <AlertDescription className="revy-cjk leading-[1.6]">
            {t(degraded ? EVIDENCE_STATE_PRESENTATION[state].labelKey : 'evidence.empty')}
          </AlertDescription>
        </Alert>
        <ConversionNotes notes={notes} t={t} />
      </section>
    );
  }

  const state = resolveEvidenceState(finding);
  const { labelKey, variant, icon: StateIcon } = EVIDENCE_STATE_PRESENTATION[state];
  const availability = resolveAvailabilityBadge(finding);

  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-2">
        <span className="revy-cjk text-sm font-medium">{t('evidence.title')}</span>
        <Badge variant={variant} className="revy-cjk gap-1">
          <StateIcon className="size-3" strokeWidth={2} />
          {t(labelKey)}
        </Badge>
        {availability && (
          <Badge variant={availability.variant} className="revy-cjk gap-1">
            <availability.icon className="size-3" strokeWidth={2} />
            {t(availability.labelKey)}
          </Badge>
        )}
        {finding.anchors.length === 0 ? (
          <Badge variant="outline" className="revy-cjk">{t('evidence.noAnchor')}</Badge>
        ) : (
          finding.anchors.map((anchor, index) => (
            <Badge key={`${anchor.targetId}-${index}`} variant="outline" className="revy-cjk">
              {t(isKnownMaturity(anchor.maturity)
                ? `evidence.anchorMaturity.${anchor.maturity}`
                : 'evidence.anchorMaturity.unknown')}
            </Badge>
          ))
        )}
        <span className="revy-cjk text-xs text-muted-foreground">{t('evidence.generatorVersion')}</span>
        <span className="revy-figure text-xs">{finding.provenance.generatorVersion}</span>
      </div>

      <ConversionNotes notes={notes} t={t} />

      {/* Keyed by index deliberately: array position IS the citation order the backend
          stored (`domain/evidence/models.py:158-162`), and two refs can legitimately share
          sourceId + contentHash. */}
      {finding.evidence.map((evidenceRef, index) => (
        <EvidenceRefRow
          key={index}
          evidenceRef={evidenceRef}
          onJump={onJump}
        />
      ))}
    </section>
  );
};

export default EvidenceRefList;
